@if (loading()) {
  <app-product-detail-skeleton />
} @else if (notFound()) {
  <app-product-not-found [reason]="notFoundReason()" (goToCatalog)="navigateHome()" />
} @else {
  <div class="detail">
    <div class="detail__container">
      <!-- Breadcrumb -->
      <app-breadcrumb [items]="breadcrumbs()" />

      <!-- Two-column layout -->
      <div class="detail__columns">
        <!-- LEFT: Image gallery -->
        <div
          class="detail__gallery"
          [class.detail__gallery--out-of-stock]="isOutOfStock()"
        >
          <app-image-gallery
            [images]="galleryImages()"
            [productName]="product()!.name"
            [variant]="product()!.variant"
            [isSingle]="isSingle()"
            [hasDoubleFace]="product()!.hasDoubleFace ?? false"
            [backImageUrl]="product()!.backImageUrl"
            (zoomClick)="onImageGalleryZoom($event)"
          >
            <div gallery-badges>
              @if (product()!.isNew) {
                <app-badge variant="new" [overlay]="true">Novedad</app-badge>
              }
              @if (product()!.isOnSale && !isOutOfStock()) {
                <app-badge variant="sale" [overlay]="true">
                  @if (discountPercent() > 0) {
                    -{{ discountPercent() }}%
                  } @else {
                    Oferta
                  }
                </app-badge>
              }
              @if (isOutOfStock()) {
                <app-badge variant="sold-out" [overlay]="true">Agotado</app-badge>
              }
              <!-- Slot PROPIO del badge VIP (R3): coexiste con «Pre-venta». -->
              @if (isEarlyAccess()) {
                <app-badge variant="vip" [overlay]="true">{{ eaBadgeText() }}</app-badge>
              }
              @if (isPreSale()) {
                <app-badge variant="pre-sale" [overlay]="true">Pre-venta</app-badge>
              }
            </div>
          </app-image-gallery>
        </div>

        <!-- RIGHT: Product info -->
        <div class="detail__info">
          <!-- Game name -->
          @if (product()!.game) {
            @if (gameLink(); as link) {
              <a class="detail__game detail__game--link" [routerLink]="link">{{ product()!.game }}</a>
            } @else {
              <p class="detail__game">{{ product()!.game }}</p>
            }
          }

          <!-- Product name (ancla del IntersectionObserver del sticky-bar
               móvil — antes apuntaba al price-block, ahora se mueve al
               name porque el price-block puede estar oculto en agotados) -->
          <div class="detail__title-row">
            <h1 class="detail__name" #priceSection>{{ product()!.name }}</h1>
            <!-- Corazón de la lista de deseos (B29, autocontenido). -->
            <app-wishlist-button class="detail__wishlist-btn" [productId]="product()!.id" [iconSize]="20" />
          </div>

          <!-- Requisito Perrón Points (Early Access) -->
          @if (isEarlyAccess()) {
            <p class="detail__ea-hint">
              <app-pp-coin [size]="16" />{{ eaRequirementHint() }}
            </p>
          }

          <!-- Set / Edition -->
          @if (product()!.setName) {
            @if (setLink(); as link) {
              <a class="detail__set detail__set--link" [routerLink]="link">
                {{ product()!.setName }}
                @if (product()!.setCode) {
                  <span class="detail__set-code">· {{ product()!.setCode }}</span>
                }
              </a>
            } @else {
              <p class="detail__set">
                {{ product()!.setName }}
                @if (product()!.setCode) {
                  <span class="detail__set-code">· {{ product()!.setCode }}</span>
                }
              </p>
            }
          }

          <!-- Brand (accessories) -->
          @if (isAccessory() && product()!.brand) {
            <p class="detail__set">{{ product()!.brand }}</p>
          }

          <!-- Variant + Condition row -->
          @if (isSingle()) {
            <div class="detail__meta-row">
              <app-variant-badge [variant]="product()!.variant">
                {{ variantLabel() }}
              </app-variant-badge>

              @if (conditionTone(); as tone) {
                <app-badge [variant]="tone">{{ conditionLabel() }}</app-badge>
              }
            </div>
          }

          <!-- Estadísticas de juego (OP/MTG). El bloque entero se oculta
               para Pokémon, legacy y no-singles. Cada item es `@if (valor)`
               para tolerar campos parciales (ej. MTG sin toughness). -->
          @if (hasGameStats()) {
            <section class="detail__game-stats" aria-label="Estadísticas de juego">
              @if (product()!.color) {
                <div class="detail__game-stat">
                  <span class="detail__game-stat-label">Color</span>
                  <span class="detail__color-chip" [attr.data-color]="colorChipKey()">
                    <span class="detail__color-dot" aria-hidden="true"></span>
                    <span class="detail__game-stat-value">{{ product()!.color }}</span>
                  </span>
                </div>
              }
              @if (product()!.mana_cost) {
                <div class="detail__game-stat">
                  <span class="detail__game-stat-label">Maná</span>
                  <span class="detail__game-stat-value detail__game-stat-value--mono">{{ product()!.mana_cost }}</span>
                </div>
              }
              @if (product()!.cost) {
                <div class="detail__game-stat">
                  <span class="detail__game-stat-label">Coste</span>
                  <span class="detail__game-stat-value detail__game-stat-value--tabular">{{ product()!.cost }}</span>
                </div>
              }
              @if (powerToughness(); as pt) {
                <div class="detail__game-stat">
                  <span class="detail__game-stat-label">{{ powerToughnessLabel() }}</span>
                  <span
                    class="detail__game-stat-value detail__game-stat-value--tabular"
                    [attr.aria-label]="product()!.toughness ? 'Poder ' + product()!.power + ', Resistencia ' + product()!.toughness : null"
                  >{{ pt }}</span>
                </div>
              }
              @if (product()!.counter) {
                <div class="detail__game-stat">
                  <span class="detail__game-stat-label">Counter</span>
                  <span class="detail__game-stat-value detail__game-stat-value--tabular">{{ product()!.counter }}</span>
                </div>
              }
              @if (product()!.card_attribute) {
                <div class="detail__game-stat">
                  <span class="detail__game-stat-label">Atributo</span>
                  <span class="detail__game-stat-value">{{ product()!.card_attribute }}</span>
                </div>
              }
              @if (subtypeChips().length) {
                <div class="detail__game-stat detail__game-stat--wide">
                  <span class="detail__game-stat-label">Subtipo</span>
                  <span class="detail__subtype-chips">
                    @for (chip of subtypeChips(); track chip) {
                      <span class="detail__subtype-chip">{{ chip }}</span>
                    }
                  </span>
                </div>
              }
            </section>
          }

          <!-- Price block (oculto en agotados — el back no envía precio) -->
          @if (!isOutOfStock()) {
            <div class="detail__price-block">
              <span class="detail__price">{{ formattedPrice() }}</span>
              @if (product()!.isOnSale && formattedOriginalPrice()) {
                <span class="detail__original-price">{{ formattedOriginalPrice() }}</span>
              }
            </div>
          }

          <!-- Stock info -->
          @if (isPreSale()) {
            <span class="detail__stock detail__stock--presale">
              Pre-venta
              @if (product()!.prepurchase_due_date) {
                · Disponible aprox. {{ product()!.prepurchase_due_date }}
              }
            </span>
          } @else if (isOutOfStock()) {
            <span class="detail__stock detail__stock--out">Sin stock</span>
          } @else if (isLowStock()) {
            <span class="detail__stock detail__stock--low">
              Últimas {{ product()!.stock }} unidades
            </span>
          } @else {
            <span class="detail__stock">{{ product()!.stock }} disponibles</span>
          }

          <!-- CTA slot (add / reserve / reserve_sold_out / notify) -->
          @switch (ctaMode()) {
            @case ('reserve_sold_out') {
              <div class="detail__actions">
                <app-button
                  variant="primary"
                  size="lg"
                  class="btn-sold-out"
                  [appTooltip]="prepurchaseSoldOutMessage"
                  (click)="onPrepurchaseSoldOutClick()"
                >
                  Precompra Agotada
                </app-button>
              </div>
            }
            @case ('notify') {
              <div class="detail__notify" aria-live="polite">
                @if (alreadySubscribed()) {
                  <p class="detail__notify-success">
                    ✓ Te avisaremos cuando esté disponible.
                  </p>
                } @else {
                  <p class="detail__notify-label">Avisarme cuando esté disponible</p>
                  <div class="detail__notify-row">
                    <app-input
                      id="notify-email"
                      type="email"
                      placeholder="tu@email.com"
                      [value]="notifyEmail()"
                      (valueChange)="notifyEmail.set($event)"
                    />
                    <app-button
                      variant="secondary"
                      size="md"
                      [disabled]="notifyStatus() === 'pending' || altaInFlight()"
                      (click)="onNotifyRequest()"
                    >
                      Avisarme
                    </app-button>
                  </div>
                  @if (notifyStatus() === 'error') {
                    <p class="detail__notify-error">
                      No pudimos registrar tu alerta. Revisa el correo e intenta de nuevo.
                    </p>
                  } @else if (notifyStatus() === 'prepurchase') {
                    <p class="detail__notify-error">
                      Este producto está en pre-venta. Revisa la sección de precompras.
                    </p>
                  }
                }
                @if (subscriberCount() > 0) {
                  <p class="detail__notify-social">
                    {{ subscriberCount() }} {{ subscriberCount() === 1 ? 'persona espera' : 'personas esperan' }} este producto.
                  </p>
                }
              </div>
            }
            @default {
              <div class="detail__actions">
                <div class="detail__quantity">
                  <button
                    class="detail__qty-btn"
                    type="button"
                    [disabled]="!canDecrement()"
                    (click)="decrementQty()"
                    aria-label="Reducir cantidad"
                  >
                    <app-icon [icon]="minusIcon" [size]="16" />
                  </button>
                  <span class="detail__qty-value">{{ quantity() }}</span>
                  <button
                    class="detail__qty-btn"
                    type="button"
                    [disabled]="!canIncrement()"
                    (click)="incrementQty()"
                    aria-label="Aumentar cantidad"
                  >
                    <app-icon [icon]="plusIcon" [size]="16" />
                  </button>
                </div>
                @if (ctaMode() === 'reserve') {
                  <app-button
                    variant="primary"
                    size="lg"
                    (click)="onReserve()"
                  >
                    Reservar
                  </app-button>
                } @else {
                  <app-button
                    variant="primary"
                    size="lg"
                    [disabled]="addStatus() === 'pending'"
                    (click)="onAddToCart()"
                  >
                    Agregar al carrito
                  </app-button>
                }
              </div>
              @if (addStatus() === 'stock_error') {
                <p class="detail__action-error">
                  {{ addErrorMessage() ?? 'No hay suficientes unidades disponibles.' }}
                </p>
              } @else if (addStatus() === 'error') {
                <p class="detail__action-error">
                  {{ addErrorMessage() }}
                </p>
              } @else if (addStatus() === 'success') {
                <p class="detail__action-success">
                  ✓ Agregado al carrito.
                </p>
              }
            }
          }

          <!-- Additional meta -->
          <div class="detail__extra-meta">
            @if (formattedLanguage(); as language) {
              <div class="detail__meta-item">
                <span class="detail__meta-label">Idioma</span>
                <span class="detail__meta-value">{{ language }}</span>
              </div>
            }
            @if (product()!.collectorNumber) {
              <div class="detail__meta-item">
                <span class="detail__meta-label"># de Colección</span>
                <span class="detail__meta-value">{{ product()!.collectorNumber }}</span>
              </div>
            }
            @if (isSingle() && product()!.rarity) {
              <div class="detail__meta-item">
                <span class="detail__meta-label">Rareza</span>
                <span class="detail__meta-value">{{ product()!.rarity }}</span>
              </div>
            }
            @if (isSingle() && product()!.cardType) {
              <div class="detail__meta-item">
                <span class="detail__meta-label">Tipo</span>
                <span class="detail__meta-value">{{ product()!.cardType }}</span>
              </div>
            }
            @if (product()!.reference) {
              <div class="detail__meta-item">
                <span class="detail__meta-label">SKU</span>
                <span class="detail__meta-value">{{ product()!.reference }}</span>
              </div>
            }
          </div>
        </div>
      </div>

      <!-- Tabs section -->
      <app-product-tabs [product]="product()!" [articles]="[]" />
    </div>

    <!-- Frecuentemente comprados juntos: bundle con checkboxes cuando el
         producto principal es comprable en el carrito normal (no agotado y no
         precompra, tenga cupos o no: isPrepurchaseProduct, N45) y queda algún
         sugerido regular con stock (D26). En otro
         caso, degradamos a un carrusel "También te puede interesar": sin
         precio del principal el total no tendría sentido, y una precompra
         no puede entrar al carrito normal (sus tarjetas desvían a reserva). -->
    @if (fbtSuggestions().length > 0) {
      @if (!isOutOfStock() && !isPrepurchaseProduct() && fbtBundleItems().length > 0) {
        <app-frequently-bought-together
          [currentProduct]="product()!"
          [suggestions]="fbtBundleItems()"
          [isAdding]="bundleAdding()"
          (addBundle)="onBundleAddToCart($event)"
        />
      } @else {
        <app-related-products
          title="También te puede interesar"
          [products]="fbtSuggestions()"
          [trailingLabel]="categoryCta()?.label ?? ''"
          [trailingLink]="categoryCta()?.link ?? ''"
          (addToCart)="onRelatedAddToCart($event)"
          (reserveClick)="onRelatedReserve($event)"
          (zoomOpen)="onRelatedZoomOpen($event)"
          (notifyRequest)="onZoomNotify($event)"
          (notifyCancel)="onZoomNotifyCancel($event)"
          (cardClick)="onRelatedCardClick($event)"
        />
      }
    }

    <!-- Productos relacionados: carrusel (max 8, variant=default). -->
    <app-related-products
      [products]="(product()!.related_products ?? []).slice(0, 8)"
      [trailingLabel]="categoryCta()?.label ?? ''"
      [trailingLink]="categoryCta()?.link ?? ''"
      (addToCart)="onRelatedAddToCart($event)"
      (reserveClick)="onRelatedReserve($event)"
      (zoomOpen)="onRelatedZoomOpen($event)"
      (notifyRequest)="onZoomNotify($event)"
      (notifyCancel)="onZoomNotifyCancel($event)"
      (cardClick)="onRelatedCardClick($event)"
    />
  </div>

  <!-- Mobile sticky bottom bar -->
  <div
    class="detail__sticky-bar"
    [class.detail__sticky-bar--visible]="stickyBarVisible()"
  >
    @if (!isOutOfStock()) {
      <span class="detail__sticky-price">{{ formattedPrice() }}</span>
    }
    @switch (ctaMode()) {
      @case ('notify') {
        <app-button
          variant="secondary"
          size="md"
          [disabled]="alreadySubscribed() || notifyStatus() === 'pending' || altaInFlight()"
          (click)="onNotifyRequest()"
        >
          @if (alreadySubscribed()) { Suscrito } @else { Avisarme }
        </app-button>
      }
      @case ('reserve') {
        <app-button
          variant="primary"
          size="md"
          (click)="onReserve()"
        >
          Reservar
        </app-button>
      }
      @case ('reserve_sold_out') {
        <app-button
          variant="primary"
          size="md"
          class="btn-sold-out"
          [appTooltip]="prepurchaseSoldOutMessage"
          (click)="onPrepurchaseSoldOutClick()"
        >
          Precompra Agotada
        </app-button>
      }
      @default {
        <app-button
          variant="primary"
          size="md"
          [disabled]="addStatus() === 'pending'"
          (click)="onAddToCart()"
        >
          Agregar al carrito
        </app-button>
      }
    }
  </div>

  <!-- Lightbox-galería: zoom con navegación entre imágenes (sin acciones de compra) -->
  @if (lightboxOpen()) {
    <app-image-lightbox
      [images]="galleryImages()"
      [initialIndex]="selectedImageIndex()"
      [alt]="product()!.name"
      [backImageUrl]="product()!.backImageUrl"
      [variant]="product()!.variant"
      (close)="onLightboxClose()"
    />
  }

  <!-- Quick-view modal: sólo desde related products (no desde la imagen principal) -->
  @if (zoomProduct()) {
    <app-product-zoom-modal
      [product]="zoomProduct()!"
      [notifyActive]="zoomNotifyActive()"
      (close)="onZoomClose()"
      (addToCart)="onZoomAddToCart($event)"
      (reserveRequest)="onZoomReserve($event)"
      (notifyRequest)="onZoomNotify($event)"
      (notifyCancel)="onZoomNotifyCancel($event)"
      (viewDetails)="onZoomViewDetails($event)"
    />
  }
}
